import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassTop as HourglassTopIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { PTGC_V2_ADDRESS } from '../utils/ownedTokensReport';

const REFRESH_TIMEOUT_MS = 120000;

const PROGRESS_STEPS = [
  { beforeMs: 0, label: 'Contacting ptadmin API' },
  { beforeMs: 1500, label: 'Calling game server /user/refreshTokens' },
  { beforeMs: 4000, label: 'Scanning Immutable zkEVM wallets' },
  { beforeMs: 10000, label: 'Scanning Avalanche C-Chain (batched RPC)' },
  { beforeMs: 25000, label: 'Enumerating ERC-721 token IDs (large PTGC wallets take longer)' },
  { beforeMs: 45000, label: 'Hydrating metadata & saving ownedTokens' },
];

function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function activeStepIndex(elapsedMs) {
  let idx = 0;
  for (let i = PROGRESS_STEPS.length - 1; i >= 0; i--) {
    if (elapsedMs >= PROGRESS_STEPS[i].beforeMs) {
      idx = i;
      break;
    }
  }
  return idx;
}

function changeColor(change) {
  if (change > 0) return 'success.main';
  if (change < 0) return 'error.main';
  return 'text.secondary';
}

export default function NftRefreshDialog({
  open,
  phase,
  elapsedMs,
  result,
  error,
  userName,
  onClose,
  onCopyDebug,
}) {
  const [copyOk, setCopyOk] = useState(false);
  const running = phase === 'running';
  const success = phase === 'success';
  const failed = phase === 'error';

  const stepIdx = activeStepIndex(elapsedMs);
  const progressPercent = running
    ? Math.min(95, 8 + (elapsedMs / REFRESH_TIMEOUT_MS) * 87)
    : success
      ? 100
      : failed
        ? 100
        : 0;

  const deltaRows = result?.delta || [];
  const ptgc = result?.highlights?.paradiseGenesisCollectionV2;
  const ptgcMissing = success && ptgc && ptgc.after === 0;

  const statusTitle = useMemo(() => {
    if (running) return 'Refreshing NFT collection…';
    if (success) return 'NFT refresh complete';
    if (failed) return 'NFT refresh failed';
    return 'NFT refresh';
  }, [running, success, failed]);

  useEffect(() => {
    if (!open) setCopyOk(false);
  }, [open]);

  const handleCopy = async () => {
    if (onCopyDebug) {
      await onCopyDebug();
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {running && <HourglassTopIcon color="primary" />}
        {success && <CheckCircleIcon color="success" />}
        {failed && <ErrorIcon color="error" />}
        {statusTitle}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              User: <strong>{userName || result?.userName || result?.userId || '—'}</strong>
              {running && (
                <>
                  {' · '}
                  Elapsed: <strong>{formatDuration(elapsedMs)}</strong>
                  {' · '}
                  Timeout: {formatDuration(REFRESH_TIMEOUT_MS)}
                </>
              )}
            </Typography>
            <LinearProgress
              variant={running ? 'determinate' : 'indeterminate'}
              value={running ? progressPercent : undefined}
              color={failed ? 'error' : success ? 'success' : 'primary'}
              sx={{ mt: 1, height: 8, borderRadius: 1 }}
            />
          </Box>

          {running && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Progress
              </Typography>
              <Stack spacing={0.75}>
                {PROGRESS_STEPS.map((step, i) => {
                  const done = i < stepIdx;
                  const active = i === stepIdx;
                  return (
                    <Typography
                      key={step.label}
                      variant="body2"
                      sx={{
                        color: done ? 'success.main' : active ? 'text.primary' : 'text.disabled',
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {done ? '✓ ' : active ? '→ ' : '· '}
                      {step.label}
                    </Typography>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {failed && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {error?.message || 'Request failed'}
              </Typography>
              {error?.isTimeout && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  The request hit the {formatDuration(REFRESH_TIMEOUT_MS)} timeout. Large wallets (100+ Genesis
                  Collection V2) can take 30–60s after the server batching fix is deployed.
                </Typography>
              )}
              {error?.gameApiUrl && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Game API: {error.gameApiUrl}
                </Typography>
              )}
            </Alert>
          )}

          {success && result && (
            <>
              <Alert severity={ptgcMissing ? 'warning' : 'success'}>
                {result.previous?.total ?? 0} → {result.current?.total ?? 0} token rows
                {result.durationMs != null && ` in ${formatDuration(result.durationMs)}`}
                {ptgcMissing && (
                  <>
                    {' — '}
                    <strong>PTGC V2 still 0</strong> in ownedTokens. Check linked wallets on-chain or server logs
                    for Avalanche RPC errors.
                  </>
                )}
              </Alert>

              {ptgc && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: ptgc.after > 0 ? 'success.50' : 'warning.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Genesis Collection V2 (PTGC)
                  </Typography>
                  <Typography variant="body2">
                    Before: <strong>{ptgc.before}</strong> · After: <strong>{ptgc.after}</strong>
                    {ptgc.change !== 0 && (
                      <>
                        {' · '}
                        Change:{' '}
                        <Box component="span" sx={{ color: changeColor(ptgc.change), fontWeight: 600 }}>
                          {ptgc.change > 0 ? '+' : ''}
                          {ptgc.change}
                        </Box>
                      </>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {PTGC_V2_ADDRESS}
                  </Typography>
                </Paper>
              )}

              {Array.isArray(result.walletAddresses) && result.walletAddresses.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Wallets scanned
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {result.walletAddresses.map((w) => (
                      <Chip key={w} label={w} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Collection breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Collection</TableCell>
                        <TableCell align="right">Before</TableCell>
                        <TableCell align="right">After</TableCell>
                        <TableCell align="right">Δ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deltaRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              No collection rows
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        deltaRows.map((row) => (
                          <TableRow
                            key={row.collection}
                            sx={{
                              bgcolor: row.collection.includes('Genesis Collection') ? 'action.hover' : undefined,
                            }}
                          >
                            <TableCell>{row.collection}</TableCell>
                            <TableCell align="right">{row.before}</TableCell>
                            <TableCell align="right">{row.after}</TableCell>
                            <TableCell align="right" sx={{ color: changeColor(row.change), fontWeight: 600 }}>
                              {row.change > 0 ? '+' : ''}
                              {row.change}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Debug
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  Game API: {result.gameApiUrl || '—'}
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  Refreshed: {result.refreshedAt || '—'}
                </Typography>
                {result.gameServer?.fetchDurationMs != null && (
                  <Typography variant="caption" component="div" color="text.secondary">
                    Game server fetch: {formatDuration(result.gameServer.fetchDurationMs)}
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {success && onCopyDebug && (
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {copyOk ? 'Copied!' : 'Copy debug JSON'}
          </Button>
        )}
        <Button onClick={onClose} disabled={running}>
          {running ? 'Running…' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { REFRESH_TIMEOUT_MS };
